if (!customElements.get('header-v4')) {
  class HeaderV4 extends HTMLElement {
    constructor() {
      super();
      this.wrapper = this.querySelector('[data-header-v4]');
      this.stickyType = this.dataset.stickyType || this.getAttribute('data-sticky-type') || 'on-scroll-up';
      this.headerSection = this.closest('.section-header');
      this.megaBackdrop = this.querySelector('[data-mega-backdrop]');
      this.megaContainer = this.querySelector('[data-mega-container]');
      this.megaTriggers = Array.from(this.querySelectorAll('[data-mega-trigger]'));
      this.megaPanels = Array.from(this.querySelectorAll('[data-mega-panel]'));
      this.localizationPanel = this.querySelector('[data-localization-panel]');
      this.localizationClose = this.querySelector('[data-localization-close]');
      this.localizationToggle = this.querySelector('[data-mobile-localization-toggle]');
      this.mobileToggles = Array.from(this.querySelectorAll('[data-mobile-toggle]'));
      this.mobileClose = this.querySelector('[data-mobile-close]');
      this.mobileDrawer = this.querySelector('[data-mobile-drawer]');
      this.mobileSearchToggles = Array.from(this.querySelectorAll('[data-mobile-search-toggle]'));
      this.mobileSearchClose = this.querySelector('[data-mobile-search-close]');
      this.mobileSearchPanel = this.querySelector('[data-mobile-search-panel]');
      this.mobileSearchInput = this.querySelector('#Search-In-Header-Mobile-V4');
      this.mobileInlineSearchInput = this.querySelector('#Search-In-Header-Mobile-Inline-V4');
      this.mobileTabbar = this.querySelector('[data-mobile-tabbar]');
      this.mobileTabbarCategoriesButton = this.querySelector('.header-v4-mobile-tabbar__button[data-mobile-tabbar-item="categories"]');
      this.mobileTabbarSearchButton = this.querySelector('.header-v4-mobile-tabbar__button[data-mobile-tabbar-item="search"]');
      this.pageCurrentTabbarItems = this.mobileTabbar
        ? Array.from(this.mobileTabbar.querySelectorAll('[aria-current="page"]'))
        : [];
      this.desktopSearchInput = this.querySelector('#Search-In-Header-V4');
      this.desktopPredictiveSearch = this.desktopSearchInput?.closest('predictive-search') || null;
      this.activeMegaIndex = -1;
      this.currentScrollTop = 0;
      this.previousWindowScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      this.hideTimer = null;
      this.scrollTimeout = null;
      this.preventReveal = false;
      this.drawerOverlay = null;
      this.connectedAt = 0;
      this.hasUserInteracted = false;
      this.hasSuppressedInitialDesktopSearchFocus = false;
      this.headerBounds = { top: 0, bottom: 0 };

      this.boundOnScroll = this.onScroll.bind(this);
      this.boundOnResize = this.onResize.bind(this);
      this.boundOnDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.boundOnDocumentClick = this.onDocumentClick.bind(this);
      this.boundCloseMegaMenu = this.closeMegaMenu.bind(this);
      this.boundCloseMobileDrawer = this.closeMobileDrawer.bind(this);
      this.boundMarkUserInteraction = this.markUserInteraction.bind(this);
      this.boundGuardDesktopSearchFocus = this.guardDesktopSearchFocus.bind(this);
      this.boundHideHeaderOnScrollUp = () => {
        this.preventReveal = true;
      };
    }

    connectedCallback() {
      this.connectedAt = window.performance?.now?.() ?? 0;
      this.createMobileOverlay();
      this.setHeaderMetrics();
      this.setupSticky();
      this.setupLocalizationPanel();
      this.setupMegaMenu();
      this.setupMobileDrawer();
      this.setupMobileSearchPanel();
      this.setupMobileTabbar();
      this.setupDesktopSearchGuard();

      window.addEventListener('resize', this.boundOnResize);
      document.addEventListener('keydown', this.boundOnDocumentKeydown);
      document.addEventListener('click', this.boundOnDocumentClick);
      document.addEventListener('pointerdown', this.boundMarkUserInteraction, true);
      document.addEventListener('keydown', this.boundMarkUserInteraction, true);
      document.addEventListener('touchstart', this.boundMarkUserInteraction, true);
      this.addEventListener('preventHeaderReveal', this.boundHideHeaderOnScrollUp);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.boundOnResize);
      window.removeEventListener('scroll', this.boundOnScroll);
      document.removeEventListener('keydown', this.boundOnDocumentKeydown);
      document.removeEventListener('click', this.boundOnDocumentClick);
      document.removeEventListener('pointerdown', this.boundMarkUserInteraction, true);
      document.removeEventListener('keydown', this.boundMarkUserInteraction, true);
      document.removeEventListener('touchstart', this.boundMarkUserInteraction, true);
      this.removeEventListener('preventHeaderReveal', this.boundHideHeaderOnScrollUp);
      this.desktopSearchInput?.removeEventListener('focus', this.boundGuardDesktopSearchFocus, true);
      this.drawerOverlay?.removeEventListener('click', this.boundCloseMobileDrawer);
      this.drawerOverlay?.remove();
    }

    markUserInteraction(event) {
      if (event.isTrusted) {
        this.hasUserInteracted = true;
      }
    }

    setupDesktopSearchGuard() {
      if (!this.desktopSearchInput) return;
      this.desktopSearchInput.addEventListener('focus', this.boundGuardDesktopSearchFocus, true);

      window.requestAnimationFrame(() => {
        if (!this.shouldSuppressInitialDesktopSearchFocus()) return;
        this.hasSuppressedInitialDesktopSearchFocus = true;
        this.desktopSearchInput.blur();
        this.closeDesktopSearchDropdown();
      });
    }

    shouldSuppressInitialDesktopSearchFocus() {
      if (!this.desktopSearchInput || this.hasUserInteracted || this.hasSuppressedInitialDesktopSearchFocus) {
        return false;
      }

      const elapsed = (window.performance?.now?.() ?? 0) - this.connectedAt;
      if (elapsed > 1500) return false;

      return document.activeElement === this.desktopSearchInput;
    }

    guardDesktopSearchFocus(event) {
      if (!this.shouldSuppressInitialDesktopSearchFocus()) return;

      this.hasSuppressedInitialDesktopSearchFocus = true;
      event.stopImmediatePropagation();

      window.requestAnimationFrame(() => {
        this.desktopSearchInput?.blur();
        this.closeDesktopSearchDropdown();
      });
    }

    closeDesktopSearchDropdown() {
      if (!this.desktopPredictiveSearch) return;

      this.desktopPredictiveSearch.removeAttribute('open');
      this.desktopPredictiveSearch.removeAttribute('loading');
      this.desktopSearchInput?.setAttribute('aria-expanded', 'false');
      this.desktopSearchInput?.removeAttribute('aria-activedescendant');
      this.desktopPredictiveSearch
        .querySelectorAll('[aria-selected="true"]')
        .forEach((element) => element.setAttribute('aria-selected', 'false'));

      const predictiveResults = this.desktopPredictiveSearch.querySelector('[data-predictive-search]');
      if (predictiveResults) {
        predictiveResults.style.maxHeight = '';
      }
    }

    createMobileOverlay() {
      if (this.drawerOverlay) return;
      this.drawerOverlay = document.createElement('div');
      this.drawerOverlay.className = 'header-v4-mobile-drawer-overlay';
      this.drawerOverlay.setAttribute('aria-hidden', 'true');
      this.drawerOverlay.hidden = true;
      this.drawerOverlay.addEventListener('click', this.boundCloseMobileDrawer);
      document.body.appendChild(this.drawerOverlay);
    }

    setupSticky() {
      if (!this.headerSection || this.stickyType === 'none') return;
      if (this.stickyType === 'always') {
        this.headerSection.classList.add('shopify-section-header-sticky');
      }
      window.addEventListener('scroll', this.boundOnScroll, { passive: true });
      this.setHeaderMetrics();
    }

    onResize() {
      this.setHeaderMetrics();
      if (window.innerWidth > 989) {
        this.closeMobileDrawer();
        this.closeMobileSearchPanel();
        this.showMobileTabbar();
      }
      if (window.innerWidth <= 989) {
        this.closeMegaMenu();
      }
    }

    setHeaderMetrics() {
      if (!this.headerSection || !this.wrapper) return;
      const headerRect = this.headerSection.getBoundingClientRect();
      const wrapperRect = this.wrapper.getBoundingClientRect();
      const previousSticky = this.headerSection.classList.contains('shopify-section-header-sticky');

      if (previousSticky) {
        this.headerSection.classList.remove('shopify-section-header-sticky', 'shopify-section-header-hidden', 'animate');
      }

      document.documentElement.style.setProperty('--header-height', `${this.wrapper.offsetHeight}px`);
      document.documentElement.style.setProperty('--header-top', `${wrapperRect.bottom}px`);
      this.headerBounds = {
        top: headerRect.top + window.scrollY,
        bottom: headerRect.bottom + window.scrollY,
      };

      if (previousSticky || this.stickyType === 'always') {
        this.headerSection.classList.add('shopify-section-header-sticky');
      }
    }

    onScroll() {
      this.updateMobileTabbarVisibility();

      if (!this.headerSection || this.stickyType === 'none' || this.stickyType === 'always') return;

      const predictiveSearchOpen = this.querySelector('predictive-search[open], predictive-search[results="true"]');
      if (predictiveSearchOpen && predictiveSearchOpen.hasAttribute('open')) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.headerSection.classList.add('shopify-section-header-sticky');
        this.headerSection.classList.remove('animate');
        this.headerSection.classList.add('shopify-section-header-hidden');
        this.closeMegaMenu();
        this.closeLocalizationPanel();
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.headerSection.classList.add('shopify-section-header-sticky');
        if (!this.preventReveal) {
          this.headerSection.classList.add('animate');
          this.headerSection.classList.remove('shopify-section-header-hidden');
        } else {
          window.clearTimeout(this.scrollTimeout);
          this.scrollTimeout = window.setTimeout(() => {
            this.preventReveal = false;
          }, 66);
          this.headerSection.classList.remove('animate');
          this.headerSection.classList.add('shopify-section-header-hidden');
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.headerSection.classList.remove('shopify-section-header-sticky', 'shopify-section-header-hidden', 'animate');
      }

      this.currentScrollTop = scrollTop;
    }

    setupMobileTabbar() {
      if (!this.mobileTabbar) return;
      this.showMobileTabbar();
    }

    showMobileTabbar() {
      if (!this.mobileTabbar) return;
      this.mobileTabbar.classList.remove('header-v4-mobile-tabbar--hidden');
    }

    updateMobileTabbarVisibility() {
      if (!this.mobileTabbar || window.innerWidth > 989) return;
      this.showMobileTabbar();
    }

    syncMobileTabbarCategoriesState(isOpen) {
      this.setMobileTabbarItemState(this.mobileTabbarCategoriesButton, isOpen);
    }

    syncMobileTabbarSearchState(isOpen) {
      this.setMobileTabbarItemState(this.mobileTabbarSearchButton, isOpen);
    }

    setMobileTabbarItemState(item, isActive) {
      if (!item) return;
      item.classList.toggle('header-v4-mobile-tabbar__item--active', isActive);
      if (item.matches('button')) {
        item.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      }
    }

    suppressTabbarCurrentStates() {
      if (!this.pageCurrentTabbarItems.length) return;
      this.pageCurrentTabbarItems.forEach((item) => {
        item.classList.remove('header-v4-mobile-tabbar__item--current');
      });
    }

    restoreTabbarCurrentStates() {
      if (!this.pageCurrentTabbarItems.length) return;
      this.pageCurrentTabbarItems.forEach((item) => {
        item.classList.add('header-v4-mobile-tabbar__item--current');
      });
    }

    setupLocalizationPanel() {
      if (!this.localizationPanel) return;
      this.localizationClose?.addEventListener('click', () => this.closeLocalizationPanel());
      this.localizationToggle?.addEventListener('click', () => {
        if (!this.localizationPanel.hidden) {
          this.closeLocalizationPanel();
          return;
        }
        this.openLocalizationPanel();
      });

      const languageOptions = this.localizationPanel.querySelectorAll('.header-v4-localization__language-option');
      languageOptions.forEach((option) => {
        option.addEventListener('click', (event) => {
          if (option.classList.contains('header-v4-localization__language-option--current')) {
            event.preventDefault();
            return;
          }
          option.classList.add('header-v4-localization__language-option--submitting');
        });
      });
    }

    openLocalizationPanel() {
      this.closeMegaMenu();
      this.closeMobileDrawer(true);
      this.closeMobileSearchPanel();
      this.localizationPanel.hidden = false;
    }

    closeLocalizationPanel() {
      if (!this.localizationPanel || this.localizationPanel.hidden) return;
      this.localizationPanel.hidden = true;
    }

    setupMegaMenu() {
      if (!this.megaContainer || !this.megaTriggers.length) return;

      this.megaTriggers.forEach((trigger, index) => {
        trigger.addEventListener('mouseenter', () => this.openMegaMenu(index));
        trigger.addEventListener('focus', () => this.openMegaMenu(index), true);
      });

      this.megaBackdrop?.addEventListener('click', this.boundCloseMegaMenu);
      this.megaContainer.addEventListener('mouseenter', () => window.clearTimeout(this.hideTimer));
      this.megaContainer.addEventListener('mouseleave', (event) => this.handleMenuLeave(event));
      this.querySelector('.header-v4-navbar')?.addEventListener('mouseleave', (event) => this.handleMenuLeave(event));

      this.megaPanels.forEach((panel) => {
        const primaryLinks = Array.from(panel.querySelectorAll('[data-mega-primary-link]'));
        primaryLinks.forEach((link) => {
          link.addEventListener('mouseenter', () => this.activatePrimaryLink(panel, link));
          link.addEventListener('focus', () => this.activatePrimaryLink(panel, link));
        });

        const secondaryLinks = Array.from(panel.querySelectorAll('[data-mega-secondary-link]'));
        secondaryLinks.forEach((link) => {
          link.addEventListener('mouseenter', () => this.activateSecondaryLink(panel, link));
          link.addEventListener('focus', () => this.activateSecondaryLink(panel, link));
        });
      });
    }

    handleMenuLeave(event) {
      const relatedTarget = event.relatedTarget;
      if (
        relatedTarget &&
        (this.megaContainer.contains(relatedTarget) ||
          this.querySelector('.header-v4-navbar')?.contains(relatedTarget) ||
          relatedTarget === this.megaBackdrop)
      ) {
        return;
      }
      this.scheduleMegaMenuClose();
    }

    openMegaMenu(index) {
      if (window.innerWidth <= 989) return;
      window.clearTimeout(this.hideTimer);
      this.closeLocalizationPanel();

      if (this.activeMegaIndex === index) return;

      if (this.activeMegaIndex >= 0) {
        this.hidePanel(this.activeMegaIndex);
      }

      this.activeMegaIndex = index;
      this.megaTriggers.forEach((trigger, triggerIndex) => {
        trigger.setAttribute('aria-expanded', triggerIndex === index ? 'true' : 'false');
      });

      this.megaBackdrop.hidden = false;
      this.megaBackdrop.setAttribute('aria-hidden', 'false');
      this.megaContainer.classList.add('header-v4-megamenu--open');
      this.megaContainer.setAttribute('aria-hidden', 'false');

      const panel = this.megaPanels[index];
      if (panel) {
        panel.hidden = false;
        const firstPrimaryLink = panel.querySelector('[data-mega-primary-link]');
        if (firstPrimaryLink) {
          this.activatePrimaryLink(panel, firstPrimaryLink, true);
        } else {
          this.showProductsPanel(panel, 'fallback');
        }
      }
    }

    scheduleMegaMenuClose() {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = window.setTimeout(() => this.closeMegaMenu(), 180);
    }

    closeMegaMenu() {
      window.clearTimeout(this.hideTimer);
      if (this.activeMegaIndex < 0) return;

      this.hidePanel(this.activeMegaIndex);
      this.activeMegaIndex = -1;
      this.megaTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.megaContainer.classList.remove('header-v4-megamenu--open');
      this.megaContainer.setAttribute('aria-hidden', 'true');
      this.megaBackdrop.setAttribute('aria-hidden', 'true');
      this.megaBackdrop.hidden = true;
    }

    hidePanel(index) {
      const panel = this.megaPanels[index];
      if (!panel) return;
      panel.hidden = true;
    }

    activatePrimaryLink(panel, primaryLink, loadProducts = false) {
      const primaryIndex = primaryLink.dataset.megaPrimaryIndex;
      const titleText = primaryLink.querySelector('.header-v4-megamenu__category-title')?.textContent?.trim() || primaryLink.textContent.trim();
      const href = primaryLink.href;
      const collectionKey = primaryLink.dataset.collectionKey || 'fallback';
      const collectionTitle = primaryLink.dataset.collectionTitle || titleText;
      const collectionUrl = primaryLink.dataset.collectionUrl || href;

      panel.querySelectorAll('.header-v4-megamenu__category-item').forEach((item) => {
        item.classList.remove('header-v4-megamenu__category-item--active');
      });
      primaryLink.closest('.header-v4-megamenu__category-item')?.classList.add('header-v4-megamenu__category-item--active');

      this.updateProductsHeader(panel, collectionTitle, collectionUrl);
      this.showProductsPanel(panel, collectionKey);
    }

    updateProductsHeader(panel, title, href) {
      const titleNode = panel.querySelector('[data-mega-products-title]');
      const linkNode = panel.querySelector('[data-mega-products-link]');
      if (titleNode) titleNode.textContent = title;
      if (linkNode) {
        linkNode.href = href;
        linkNode.textContent = `View all in ${title}`;
        const icon = document.createElement('span');
        icon.className = 'svg-wrapper';
        icon.innerHTML = this.getCaretSvg();
        linkNode.appendChild(icon);
      }
    }

    getCaretSvg() {
      return '<svg aria-hidden="true" focusable="false" viewBox="0 0 10 6"><path fill="currentColor" d="M5 6 0 1l1-1 4 4 4-4 1 1z"></path></svg>';
    }

    showProductsPanel(panel, collectionKey) {
      const productPanels = Array.from(panel.querySelectorAll('[data-products-panel]'));
      if (!productPanels.length) return;

      let hasMatch = false;
      productPanels.forEach((productPanel) => {
        const isActive = productPanel.dataset.productsPanel === collectionKey;
        productPanel.hidden = !isActive;
        productPanel.classList.toggle('hidden', !isActive);
        if (isActive) hasMatch = true;
      });

      if (!hasMatch) {
        const fallbackPanel = panel.querySelector('[data-products-panel="fallback"]');
        if (fallbackPanel) {
          fallbackPanel.hidden = false;
          fallbackPanel.classList.remove('hidden');
        }
      }
    }

    setupMobileDrawer() {
      if (!this.mobileToggles.length || !this.mobileDrawer) return;
      this.mobileToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
          if (toggle.getAttribute('aria-expanded') === 'true') {
            this.closeMobileDrawer();
          } else {
            this.openMobileDrawer();
          }
        });
      });
      this.mobileClose?.addEventListener('click', this.boundCloseMobileDrawer);

      this.mobileDrawer.addEventListener('toggle', (event) => {
        const details = event.target;
        if (details.tagName !== 'DETAILS' || !details.open) return;
        const parentList = details.parentElement?.parentElement;
        if (!parentList || parentList.tagName !== 'UL') return;
        parentList.querySelectorAll('li > details[open]').forEach((sibling) => {
          if (sibling !== details) sibling.open = false;
        });
      });
    }

    setupMobileSearchPanel() {
      if (!this.mobileSearchToggles.length || !this.mobileSearchPanel) return;
      this.mobileSearchToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
          if (toggle.getAttribute('aria-expanded') === 'true') {
            this.closeMobileSearchPanel();
          } else {
            this.openMobileSearchPanel();
          }
        });
      });
      this.mobileSearchClose?.addEventListener('click', () => this.closeMobileSearchPanel());
    }

    syncMobileSearchToggleState(isOpen) {
      this.mobileSearchToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
      this.syncMobileTabbarSearchState(isOpen);
    }

    isClickWithinMobileSearchToggle(target) {
      return this.mobileSearchToggles.some((toggle) => toggle.contains(target));
    }

    openMobileSearchPanel() {
      this.closeMegaMenu();
      this.closeLocalizationPanel();
      this.closeMobileDrawer(true);

      this.suppressTabbarCurrentStates();
      this.syncMobileSearchToggleState(true);

      if (this.mobileInlineSearchInput && window.innerWidth <= 989) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        window.requestAnimationFrame(() => this.mobileInlineSearchInput.focus());
        return;
      }

      if (this.mobileSearchPanel) this.mobileSearchPanel.hidden = false;
      window.requestAnimationFrame(() => this.mobileSearchInput?.focus());
    }

    closeMobileSearchPanel() {
      this.restoreTabbarCurrentStates();
      this.syncMobileSearchToggleState(false);
      if (this.mobileInlineSearchInput && window.innerWidth <= 989) {
        this.mobileInlineSearchInput.blur();
        this.mobileInlineSearchInput.value = '';
        return;
      }
      if (this.mobileSearchPanel) this.mobileSearchPanel.hidden = true;
    }

    openMobileDrawer() {
      this.closeMegaMenu();
      this.closeLocalizationPanel();
      this.closeMobileSearchPanel();
      this.mobileDrawer.hidden = false;
      this.drawerOverlay.hidden = false;
      this.mobileDrawer.classList.add('header-v4-mobile-drawer--open');
      this.mobileToggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
      this.suppressTabbarCurrentStates();
      this.syncMobileTabbarCategoriesState(true);
      this.drawerOverlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
    }

    closeMobileDrawer(skipAnimation = false) {
      if (!this.mobileDrawer || this.mobileDrawer.hidden) return;

      this.mobileDrawer.classList.remove('header-v4-mobile-drawer--open');
      this.mobileToggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
      this.restoreTabbarCurrentStates();
      this.syncMobileTabbarCategoriesState(false);
      this.drawerOverlay?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');

      if (skipAnimation) {
        if (this.drawerOverlay) this.drawerOverlay.hidden = true;
        this.mobileDrawer.hidden = true;
        return;
      }

      const handleTransitionEnd = () => {
        if (!this.mobileDrawer) return;
        if (this.drawerOverlay) this.drawerOverlay.hidden = true;
        this.mobileDrawer.hidden = true;
        this.mobileDrawer.removeEventListener('transitionend', handleTransitionEnd);
      };
      this.mobileDrawer.addEventListener('transitionend', handleTransitionEnd, { once: true });
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape') return;
      this.closeMegaMenu();
      this.closeLocalizationPanel();
      this.closeMobileDrawer();
      this.closeMobileSearchPanel();
    }

    onDocumentClick(event) {
      if (this.localizationPanel && !this.localizationPanel.hidden) {
        const clickedInsideLocalization = this.localizationPanel.contains(event.target);
        const clickedLocalizationToggle = this.localizationToggle?.contains(event.target);
        if (!clickedInsideLocalization && !clickedLocalizationToggle) {
          this.closeLocalizationPanel();
        }
      }

      if (this.mobileSearchPanel && !this.mobileSearchPanel.hidden) {
        const clickedInsideSearch = this.mobileSearchPanel.contains(event.target);
        const clickedSearchToggle = this.isClickWithinMobileSearchToggle(event.target);
        if (!clickedInsideSearch && !clickedSearchToggle) {
          this.closeMobileSearchPanel();
        }
      }
    }
  }

  customElements.define('header-v4', HeaderV4);
}
